// Single source of truth for property listing pages.
// Used by page-level Schema.org markup and by scripts/generate-sitemap.ts.

export const SITE_URL = "https://sohar.sa";

export interface ListingImage {
  loc: string; // absolute URL
  caption: string;
  title: string;
}

export interface Listing {
  path: string;
  nameAr: string;
  nameEn: string;
  descriptionAr: string;
  descriptionEn: string;
  district: string; // English district name
  availability: "InStock" | "SoldOut" | "PreOrder";
  numberOfUnits?: number;
  floorSizeMin: number;
  floorSizeMax: number;
  priority: string;
  images: ListingImage[];
}

const img = (file: string, caption: string, title: string): ListingImage => ({
  loc: `${SITE_URL}/og/${file}`,
  caption,
  title,
});

export const listings: Listing[] = [
  {
    path: "/adwar",
    nameAr: "أدوار النرجس",
    nameEn: "Adwar Al Narjis",
    descriptionAr:
      "27 دور سكني فاخر في حي النرجس بالرياض — مساحات 180-250 م²، تشطيبات فاخرة، تكييف مخفي، عدادات مستقلة وضمانات شاملة.",
    descriptionEn:
      "27 luxury separate residential floors in Al Narjis district, Riyadh. Areas 180-250 m² with luxury finishes.",
    district: "Al Narjis, Riyadh",
    availability: "InStock",
    numberOfUnits: 27,
    floorSizeMin: 180,
    floorSizeMax: 250,
    priority: "0.8",
    images: [img("adwar.jpg", "أدوار النرجس - صحار العقارية", "Adwar Al Narjis")],
  },
  {
    path: "/aqiq",
    nameAr: "أدوار العقيق",
    nameEn: "Adwar Al Aqiq",
    descriptionAr:
      "أدوار سكنية فاخرة في حي العقيق بالرياض بتصاميم عصرية ومواقع مميزة قريبة من أهم الخدمات.",
    descriptionEn:
      "Luxury residential floors in Al Aqiq district, Riyadh, with modern designs and a prime location.",
    district: "Al Aqiq, Riyadh",
    availability: "InStock",
    numberOfUnits: 9,
    floorSizeMin: 185,
    floorSizeMax: 245,
    priority: "0.8",
    images: [img("aqiq.jpg", "أدوار العقيق - صحار العقارية", "Adwar Al Aqiq")],
  },
  {
    path: "/narjis",
    nameAr: "فلل النرجس",
    nameEn: "Al Narjis Villas",
    descriptionAr:
      "6 فلل سكنية فاخرة بمساحات 207-230 م² في حي النرجس بالرياض، 3 نماذج سكنية وتشطيبات عالية الجودة.",
    descriptionEn:
      "6 luxury residential villas with areas 207-230 m² in Al Narjis, Riyadh.",
    district: "Al Narjis, Riyadh",
    availability: "SoldOut",
    numberOfUnits: 6,
    floorSizeMin: 207,
    floorSizeMax: 230,
    priority: "0.7",
    images: [img("narjis.jpg", "فلل النرجس - صحار العقارية", "Al Narjis Villas")],
  },
  {
    path: "/qairawan",
    nameAr: "فلل القيروان",
    nameEn: "Al Qairawan Villas",
    descriptionAr:
      "فلل فاخرة بمساحات بناء 483-570 م² في حي القيروان بالرياض قرب طريق الملك سلمان بتشطيبات مميزة.",
    descriptionEn:
      "2 luxury villa models with building areas 483-570 m² in Al Qairawan, Riyadh.",
    district: "Al Qairawan, Riyadh",
    availability: "SoldOut",
    numberOfUnits: 2,
    floorSizeMin: 483,
    floorSizeMax: 570,
    priority: "0.7",
    images: [img("qairawan.jpg", "فلل القيروان - صحار العقارية", "Al Qairawan Villas")],
  },
];

export const organizationSchema = {
  "@context": "https://schema.org",
  "@type": "RealEstateAgent",
  "@id": `${SITE_URL}/#organization`,
  name: "Sohar Real Estate",
  alternateName: "صحار العقارية",
  url: SITE_URL,
  logo: `${SITE_URL}/og/home.jpg`,
  image: `${SITE_URL}/og/home.jpg`,
  telephone: "+966115001577",
  areaServed: { "@type": "City", name: "Riyadh" },
  address: {
    "@type": "PostalAddress",
    addressLocality: "Riyadh",
    addressRegion: "Riyadh Province",
    addressCountry: "SA",
  },
  sameAs: ["https://x.com/soharsa"],
};

export function listingSchema(listing: Listing, lang: "ar" | "en" = "ar") {
  const url = `${SITE_URL}${listing.path}`;
  const name = lang === "ar" ? listing.nameAr : listing.nameEn;
  const description = lang === "ar" ? listing.descriptionAr : listing.descriptionEn;

  return [
    {
      "@context": "https://schema.org",
      "@type": "RealEstateListing",
      "@id": `${url}#listing`,
      url,
      name,
      description,
      inLanguage: lang,
      image: listing.images.map((i) => i.loc),
      datePosted: undefined,
      provider: { "@id": `${SITE_URL}/#organization` },
      containedInPlace: {
        "@type": "Place",
        name: listing.district,
        address: {
          "@type": "PostalAddress",
          addressLocality: "Riyadh",
          addressCountry: "SA",
        },
      },
      about: {
        "@type": "Residence",
        name,
        numberOfAccommodationUnits: listing.numberOfUnits,
        floorSize: {
          "@type": "QuantitativeValue",
          minValue: listing.floorSizeMin,
          maxValue: listing.floorSizeMax,
          unitCode: "MTK",
        },
      },
      offers: {
        "@type": "Offer",
        url,
        availability: `https://schema.org/${listing.availability}`,
        priceCurrency: "SAR",
        seller: { "@id": `${SITE_URL}/#organization` },
        areaServed: { "@type": "Place", name: `${listing.district}, Saudi Arabia` },
      },
    },
    organizationSchema,
  ] as Record<string, unknown>[];
}
